(() => {
  const body = document.body
  const selectors = []

  if (body.classList.contains('character-roll-buttons-enabled')) {
    selectors.push(
      'button:not([data-character-animation="off"])',
      '.button:not(input):not([data-character-animation="off"])',
      '.shopify-challenge__button:not(input):not([data-character-animation="off"])',
      '.shopify-payment-button__button--unbranded:not(input):not([data-character-animation="off"])',
    )
  }

  if (body.classList.contains('character-roll-links-enabled')) {
    selectors.push(
      '.header-wrapper a:not([data-character-animation="off"])',
      '.header-wrapper summary.header__menu-item:not([data-character-animation="off"])',
      '.header-wrapper summary.menu-drawer__menu-item:not([data-character-animation="off"])',
      '.footer a:not([data-character-animation="off"])',
      'body.password .password-header a:not([data-character-animation="off"])',
      'body.password footer a:not([data-character-animation="off"])',
    )
  }

  if (!selectors.length) return

  const targetSelector = selectors.join(',')
  const interactiveSelector = 'a, button, summary, [role="button"]'
  const excludedContentSelector =
    'script, style, svg, template, [hidden], .hidden, .visually-hidden, .loading__spinner, [aria-hidden="true"], .character-roll__text'
  const generatedLabelAttribute = 'data-character-roll-generated-label'
  const targetTriggers = new WeakMap()
  const segmenter =
    typeof Intl.Segmenter === 'function'
      ? new Intl.Segmenter(document.documentElement.lang || undefined, { granularity: 'grapheme' })
      : null

  let observer
  let refreshFrame
  const pendingTargets = new Set()

  const splitCharacters = (text) =>
    segmenter ? Array.from(segmenter.segment(text), ({ segment }) => segment) : Array.from(text)

  const isExcludedTextNode = (node, target) => {
    let parent = node.parentElement

    while (parent && parent !== target) {
      if (parent.matches(excludedContentSelector)) return true
      parent = parent.parentElement
    }

    return false
  }

  const getTextNodes = (target) => {
    const nodes = []
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue?.trim() || isExcludedTextNode(node, target)) return NodeFilter.FILTER_REJECT
        return NodeFilter.FILTER_ACCEPT
      },
    })

    while (walker.nextNode()) nodes.push(walker.currentNode)
    return nodes
  }

  const unwrapTarget = (target) => {
    target.querySelectorAll('.character-roll__text').forEach((wrapper) => {
      wrapper.replaceWith(document.createTextNode(wrapper.dataset.originalText || wrapper.textContent || ''))
    })
  }

  const clearGeneratedLabel = (target) => {
    if (!target.hasAttribute(generatedLabelAttribute)) return
    target.removeAttribute('aria-label')
    target.removeAttribute(generatedLabelAttribute)
  }

  const updateAccessibleName = (target, label) => {
    if (!target.matches(interactiveSelector)) return false

    if (target.hasAttribute('aria-labelledby')) {
      clearGeneratedLabel(target)
      return false
    }

    if (target.hasAttribute(generatedLabelAttribute)) {
      target.setAttribute('aria-label', label)
      return true
    }

    if (!target.hasAttribute('aria-label')) {
      target.setAttribute('aria-label', label)
      target.setAttribute(generatedLabelAttribute, '')
    }

    return true
  }

  const cleanupTarget = (target) => {
    unwrapTarget(target)
    target.classList.remove('character-roll')
    clearGeneratedLabel(target)

    const trigger = targetTriggers.get(target)
    if (trigger && trigger !== target && !trigger.querySelector('.character-roll')) {
      trigger.classList.remove('character-roll-trigger')
    } else if (trigger === target) {
      trigger.classList.remove('character-roll-trigger')
    }
    targetTriggers.delete(target)
  }

  const prepareTarget = (target) => {
    if (
      !target.isConnected ||
      target.matches('input, [hidden], .hidden, .visually-hidden, [aria-hidden="true"], [data-character-animation="off"]')
    ) {
      cleanupTarget(target)
      return
    }

    unwrapTarget(target)
    const textNodes = getTextNodes(target)
    const label = textNodes
      .map((node) => node.nodeValue.trim())
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/gu, ' ')

    if (!label) {
      cleanupTarget(target)
      return
    }

    const trigger = target.matches(interactiveSelector) ? target : target.closest(interactiveSelector) || target
    const hideVisualText = updateAccessibleName(target, label)
    let characterIndex = 0

    textNodes.forEach((textNode) => {
      const originalText = textNode.nodeValue
      const match = originalText.match(/^(\s*)([\s\S]*?\S)(\s*)$/u)
      if (!match) return

      const [, leadingWhitespace, meaningfulText, trailingWhitespace] = match
      const normalizedText = meaningfulText.replace(/\s+/gu, ' ')
      const replacement = document.createDocumentFragment()
      const textWrapper = document.createElement('span')

      if (leadingWhitespace) replacement.append(document.createTextNode(leadingWhitespace))

      textWrapper.className = 'character-roll__text'
      textWrapper.dataset.originalText = meaningfulText
      if (hideVisualText) textWrapper.setAttribute('aria-hidden', 'true')

      splitCharacters(normalizedText).forEach((character) => {
        const displayCharacter = character === ' ' ? '\u00a0' : character
        const characterWrapper = document.createElement('span')
        const currentCharacter = document.createElement('span')

        characterWrapper.className = 'character-roll__character'
        characterWrapper.dataset.character = displayCharacter
        characterWrapper.style.setProperty('--character-roll-index', characterIndex)
        currentCharacter.className = 'character-roll__current'
        currentCharacter.textContent = displayCharacter
        characterWrapper.append(currentCharacter)
        textWrapper.append(characterWrapper)
        characterIndex += 1
      })

      replacement.append(textWrapper)
      if (trailingWhitespace) replacement.append(document.createTextNode(trailingWhitespace))
      textNode.replaceWith(replacement)
    })

    target.classList.add('character-roll')
    trigger.classList.add('character-roll-trigger')
    targetTriggers.set(target, trigger)
  }

  const isOutermostTarget = (target) => !target.parentElement?.closest(targetSelector)

  const getTargetsWithin = (root) => {
    const targets = new Set()
    if (root instanceof Element && root.matches(targetSelector)) targets.add(root)
    root.querySelectorAll?.(targetSelector).forEach((target) => targets.add(target))
    return Array.from(targets).filter(isOutermostTarget)
  }

  const observe = () => {
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['aria-disabled', 'aria-hidden', 'class', 'disabled', 'hidden'],
      characterData: true,
      childList: true,
      subtree: true,
    })
  }

  const processTargets = (targets) => {
    observer.disconnect()
    targets.forEach((target) => prepareTarget(target))
    observe()
  }

  const queueTarget = (node) => {
    const element = node instanceof Element ? node : node.parentElement
    const target = element?.closest(targetSelector)
    if (target && isOutermostTarget(target)) pendingTargets.add(target)
  }

  const flushPendingTargets = () => {
    refreshFrame = undefined
    const targets = Array.from(pendingTargets).filter((target) => target.isConnected)
    pendingTargets.clear()
    if (targets.length) processTargets(targets)
  }

  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      queueTarget(mutation.target)
      mutation.addedNodes.forEach((node) => {
        queueTarget(node)
        if (node instanceof Element) getTargetsWithin(node).forEach((target) => pendingTargets.add(target))
      })
    })

    if (pendingTargets.size && !refreshFrame) refreshFrame = requestAnimationFrame(flushPendingTargets)
  })

  getTargetsWithin(document).forEach((target) => prepareTarget(target))
  observe()

  document.addEventListener('shopify:section:load', (event) => {
    const targets = getTargetsWithin(event.target)
    if (targets.length) processTargets(targets)
  })
})()
